import { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type {
  AccountSubscription,
  PlanPeriod,
} from '@frameio/graphql-client/generated';
import { useGetCurrentPlanQuery } from '@frameio/graphql-client/generated';
import { useCurrentPlanTracking } from '@frameio/shared/plans/hooks';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { formatBytes, formatMoney } from '@frameio/utils/formatters';
import {
  Button2,
  DialogActions,
  DialogFooter,
  IconButton2,
  LoadingContainer,
  Text,
  theme,
} from '@frameio/vapor';
import { IconMinus, IconPlus } from '@frameio/vapor-icons';
import { STORAGE_VARIANT_CONFIG } from '../../../plans/components/storageVariantConfig';
import { getPeriodText } from '../../../plans/constants';
import { ACCOUNT_CONVERSION_STEPS } from './constants';
import TEXT from './generated/TEXT/ManageStorage';
import styles from './ManageStorage.module.css';
import type { PlanQuoteAddOns } from './PlanSummary';
import type { StorageVariant } from './types';

TEXT.DEFINE({
  /** Button text to cancel storage management */
  cancel: 'Cancel',
  /** Button text to continue with storage changes */
  continue: 'Continue',
  /** Text describing included storage in plan */
  planIncludedStorage: (
    formattedIncludedStorage: string,
    storageType: string
  ): string =>
    `Your plan includes ${formattedIncludedStorage} of ${storageType}.`,
  /** Accessibility label for increase storage button */
  increaseStorage: 'Increase storage',
  /** Accessibility label for decrease storage button */
  decreaseStorage: 'Decrease storage',
  /** Text for adding extra storage */
  extraStorageAdding: ({
    formattedFileSize,
    formattedPrice,
    period,
    formattedIncrement,
    storageType,
  }: {
    formattedFileSize: string;
    formattedPrice: string;
    period: string;
    formattedIncrement: string;
    storageType: string;
  }): string =>
    `Adding ${formattedFileSize} extra ${storageType} (${formattedPrice} /${period} per ${formattedIncrement})`,
  /** Text for removing extra storage */
  extraStorageRemoving: ({
    formattedFileSize,
    formattedPrice,
    period,
    formattedIncrement,
    storageType,
  }: {
    formattedFileSize: string;
    formattedPrice: string;
    period: string;
    formattedIncrement: string;
    storageType: string;
  }): string =>
    `Removing ${formattedFileSize} extra ${storageType} (${formattedPrice} /${period} per ${formattedIncrement})`,
  /** Text describing plan cost increase */
  planCostIncrease: (costChange: number, period: string): string =>
    `Your plan cost will increase by $${costChange} /${period} (plus tax)`,
  /** Text describing plan cost decrease */
  planCostDecrease: (costChange: number, period: string): string =>
    `Your plan cost will decrease by $${costChange} /${period} (plus tax)`,
  /** Text describing current extra storage amount */
  currentStorage: (
    formattedExtraStorage: string,
    storageType: string
  ): string => `You have ${formattedExtraStorage} of extra ${storageType}`,
  /** Text describing current storage price per unit */
  currentPerPrice: (
    formattedPrice: string,
    period: string,
    formattedIncrement: string
  ): string =>
    `${formattedPrice} /${period} per ${formattedIncrement} (plus tax)`,
  /** Toast description for storage added */
  actionDescriptionAdded: (
    formattedFileSize: string,
    storageType: string
  ): string => `${formattedFileSize} of ${storageType} was added to your plan.`,
  /** Toast description for storage removed */
  actionDescriptionRemoved: (
    formattedFileSize: string,
    storageType: string
  ): string =>
    `${formattedFileSize} of ${storageType} was removed from your plan.`,
  /** Loading label for content */
  loadingLabel: 'Loading content...',
});

const getStorageAddOn = (
  addOns: AccountSubscription['addOns'],
  variant: StorageVariant
) => {
  if (!addOns) return null;
  const { addOnModifier } = STORAGE_VARIANT_CONFIG[variant];
  return addOns.find((addOn) => addOn.modifier === addOnModifier);
};

type ManageStorageProps = {
  accountId: string;
  onCancel: () => void;
  period: PlanPeriod;
  planQuoteAddOns?: { quantity: number };
  setParentStep: (step: ACCOUNT_CONVERSION_STEPS) => void;
  setPlanQuoteAddOns: (addOns: PlanQuoteAddOns) => void;
  variant?: StorageVariant;
};

export const ManageStorage = ({
  accountId,
  onCancel,
  period,
  planQuoteAddOns,
  setParentStep,
  setPlanQuoteAddOns,
  variant = 'active',
}: ManageStorageProps) => {
  const { data, loading: isLoadingCurrentPlan } = useGetCurrentPlanQuery({
    fetchPolicy: 'cache-and-network',
    variables: { accountId },
    skip: !accountId,
  });

  const subscription = data?.account?.subscription;
  const config = STORAGE_VARIANT_CONFIG[variant];

  const totalStorage = config.getTotalStorage(subscription);
  const includedStorage = config.getIncludedStorage(subscription);
  const perUserStorage = config.getPerUserStorage(subscription);

  const addOns = subscription?.addOns;
  const storageAddOns = getStorageAddOn(addOns, variant);
  const storageAddOnsQuanity = storageAddOns?.quantity ?? 0;
  const storageAddOnsIncrement = storageAddOns?.increment ?? 0;
  const storageAddOnsCost = storageAddOns?.cost ?? 0;
  const storageAddOnsId = storageAddOns?.id;
  const [increment, setIncrement] = useState(planQuoteAddOns?.quantity ?? 0);

  const isIncreasing = storageAddOnsQuanity + increment > storageAddOnsQuanity;
  const noAddOnStorage = storageAddOnsQuanity + increment < 1;
  const titleColor = !isIncreasing ? 'primary' : 'success';

  const currentPlanTracking = useCurrentPlanTracking();
  const track = useTrackUnsafe();

  const getExtraStorageText = () => {
    const decrease = Math.abs(
      (storageAddOnsQuanity - (storageAddOnsQuanity - increment)) *
        storageAddOnsIncrement
    );
    const increase = storageAddOnsIncrement * increment;

    const formattedFileSize = formatBytes(isIncreasing ? increase : decrease);

    const formattedStorageAddOnsIncrement = formatBytes(storageAddOnsIncrement);

    const formattedPrice = formatMoney(storageAddOnsCost, false);
    const periodLabel = getPeriodText()[period];

    const storageTextParams = {
      formattedFileSize,
      formattedPrice,
      period: periodLabel,
      formattedIncrement: formattedStorageAddOnsIncrement,
      storageType: config.storageTypeLabel,
    };
    return isIncreasing
      ? TEXT.extraStorageAdding(storageTextParams)
      : TEXT.extraStorageRemoving(storageTextParams);
  };

  const getPlanCostText = () => {
    const decreaseCost = Math.abs(
      (storageAddOnsQuanity - (storageAddOnsQuanity - increment)) *
        storageAddOnsCost
    );
    const costChange = isIncreasing
      ? storageAddOnsCost * increment
      : decreaseCost;
    const periodLabel = getPeriodText()[period];

    return isIncreasing
      ? TEXT.planCostIncrease(costChange, periodLabel)
      : TEXT.planCostDecrease(costChange, periodLabel);
  };

  const getActionDescriptionText = () => {
    const decrease = Math.abs(
      (storageAddOnsQuanity - (storageAddOnsQuanity - increment)) *
        storageAddOnsIncrement
    );
    const increase = storageAddOnsIncrement * increment;

    const formattedFileSize = formatBytes(isIncreasing ? increase : decrease);

    const storageType = config.storageTypeLabel;

    return isIncreasing
      ? TEXT.actionDescriptionAdded(formattedFileSize, storageType)
      : TEXT.actionDescriptionRemoved(formattedFileSize, storageType);
  };

  const actionDescription = getActionDescriptionText();

  const handleSubmit = useCallback(() => {
    track(config.quantitySetEvent, currentPlanTracking);
    setPlanQuoteAddOns({
      quantity: increment + storageAddOnsQuanity,
      id: storageAddOnsId ?? '',
      actionDescription,
    });
    setParentStep(ACCOUNT_CONVERSION_STEPS.SUMMARY);
  }, [
    actionDescription,
    config.quantitySetEvent,
    currentPlanTracking,
    increment,
    setParentStep,
    setPlanQuoteAddOns,
    storageAddOnsId,
    storageAddOnsQuanity,
    track,
  ]);

  const extraStorage = storageAddOnsIncrement * increment;

  const formattedCurrentStorage = formatBytes(totalStorage + extraStorage);

  const noIncrementalChange = increment === 0;
  const isDisabled = noIncrementalChange;
  const isLoading = isLoadingCurrentPlan;
  const showStorageAddonDetails = storageAddOnsQuanity > 0 || increment > 0;

  return (
    <LoadingContainer isLoading={isLoading} loadingLabel={TEXT.loadingLabel}>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.iconButton}>
            <IconButton2
              icon={<IconMinus />}
              isDisabled={noAddOnStorage}
              label={TEXT.decreaseStorage}
              onPress={() => setIncrement(increment - 1)}
              shape="circular"
            />
          </div>

          <AnimatePresence mode="sync">
            <motion.div
              key={formattedCurrentStorage}
              animate={{
                opacity: 1,
              }}
              className={styles.currentStorageWrapper}
              exit={{
                opacity: 0,
              }}
              initial={{
                opacity: 0,
              }}
              transition={{
                duration: 0.2,
                easings: theme.transitions.eases.primary,
              }}
            >
              <Text color={titleColor} fontWeight="medium" variant="titleLarge">
                {formattedCurrentStorage}
              </Text>
            </motion.div>
          </AnimatePresence>

          <div className={styles.iconButton}>
            <IconButton2
              icon={<IconPlus />}
              label={TEXT.increaseStorage}
              onPress={() => setIncrement(increment + 1)}
              shape="circular"
            />
          </div>
        </div>

        <div className={styles.includedStorageWrapper}>
          <Text color="secondary" fontWeight="regular" variant="caption">
            {TEXT.planIncludedStorage(
              formatBytes(includedStorage ?? 0),
              config.storageTypeLabel
            )}
          </Text>
        </div>

        <AnimatePresence>
          {showStorageAddonDetails && (
            <motion.div
              animate={{
                height: 'auto',
                opacity: 1,
              }}
              className={styles.animatedDetailsWrapper}
              exit={{
                height: 0,
                opacity: 0,
              }}
              initial={{
                height: 0,
                opacity: 0,
              }}
              transition={{
                duration: 0.175,
                easings: theme.transitions.eases.primary,
              }}
            >
              <div className={styles.details}>
                {noIncrementalChange ? (
                  <>
                    <Text color="primary" variant="body">
                      {TEXT.currentStorage(
                        formatBytes(
                          (totalStorage ?? 0) -
                            (includedStorage ?? 0) -
                            (perUserStorage ?? 0)
                        ),
                        config.storageTypeLabel
                      )}
                    </Text>
                    <Text color="secondary" variant="secondary">
                      {TEXT.currentPerPrice(
                        formatMoney(storageAddOnsCost, false),
                        getPeriodText()[period],
                        formatBytes(storageAddOnsIncrement)
                      )}
                    </Text>
                  </>
                ) : (
                  <>
                    <Text color="primary" fontWeight="medium" variant="body">
                      {getExtraStorageText()}
                    </Text>

                    <Text color="secondary" variant="secondary">
                      {getPlanCostText()}
                    </Text>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <DialogFooter>
        <DialogActions>
          <Button2 onPress={onCancel}>{TEXT.cancel}</Button2>

          <Button2
            isDisabled={isDisabled}
            onPress={handleSubmit}
            variant="primary"
          >
            {TEXT.continue}
          </Button2>
        </DialogActions>
      </DialogFooter>
    </LoadingContainer>
  );
};
